import request from 'src/lib/request';
import { all, select, call, put, take, takeLatest } from 'redux-saga/effects';
import { getModel, utils as macheteReduxUtils } from 'src/machete-redux';
import trackError from 'src/lib/rollbar';
import appDux from 'src/redux/app/';
import measureModeDux from 'src/redux/measureMode';
import uiDux from 'src/redux/ui';
import macheteDux from 'src/redux/machete';
import Measurements from 'src/models/Measurements';
import constants from 'src/constants';
import utils from 'src/utils';

const { MEASUREMENTS_LABELS } = constants.app;
const { VIEW_OPTIONS, SEGMENTS_VISIBILITY_OPTS } = constants.machete;
const {
  CALCULATE_MEASUREMENTS_FAILED,
  FETCHING_MEASUREMENTS_FAILED,
} = constants.errors;
const { isSegmentsCategory, flattenSegmentsItems } = utils.measure;
const { isRoofOnly: isRoofOnlyProperty } = utils.property;

export function* fetchSegmentsData() {
  let segmentsData = yield select(measureModeDux.selectors.getSegmentsData);

  if (!segmentsData) {
    try {
      const url = yield select(measureModeDux.selectors.getUrlForSegmentsData);
      if (url) {
        const apiResponse = yield call([request, 'get'], url);
        segmentsData = apiResponse.data;
      }
    } catch (e) {
      console.log('Error fetching pro premium measurements: ', e);
      trackError(FETCHING_MEASUREMENTS_FAILED, e);
    }
  }

  yield put(appDux.actions.setSegmentsData(segmentsData));
}

export function* calculateMeasurementsSaga() {
  try {
    const labels = yield select(macheteDux.selectors.getLabels);
    const segmentsData = yield select(measureModeDux.selectors.getSegmentsData);
    const jobDetails = yield select(appDux.selectors.getJobDetails);
    const model = getModel();
    const labelAreas = model.computeLabelAreas();
    const { elements, annotations } = model;
    const isRoofOnly = isRoofOnlyProperty(jobDetails.deliverable_id);
    const useWindowConsistentMeasurements =
      model.windowMeasurementsVersion === '2.0';
    const measurements = new Measurements({
      labels,
      labelAreas,
      elements,
      segmentsData,
      annotations,
      isRoofOnly,
      useWindowConsistentMeasurements,
    });

    const measurementData = {
      ...measurements.getSegments(),
      ...measurements.getMainMaterials(),
      [MEASUREMENTS_LABELS.surfaceAreas]: measurements.getSurfaceAreas(),
      [MEASUREMENTS_LABELS.openings]: measurements.getOpenings(),
    };

    yield put(measureModeDux.actions.updateMeasurements(measurementData));
  } catch (e) {
    yield put(appDux.actions.activateErrorModal());
    trackError(CALCULATE_MEASUREMENTS_FAILED, e);
  }
}

export function* activateMeasureModeSaga() {
  const isProductCatalogInitialized = yield select(
    appDux.selectors.isProductCatalogInitialized,
  );

  /* we're actually waiting for the model to be textured
  if its not textured, there will be rendering errors */
  if (!isProductCatalogInitialized) {
    yield take(appDux.actions.setProductCatalogInitialized);
  }

  yield call(fetchSegmentsData);
}

export function* setActiveCategoryNameSaga(action) {
  const { categoryName } = action.payload;
  const {
    setDefaultVisibility,
    onlyOutlineAllFacades,
    addOutlineToSegments,
  } = macheteReduxUtils;

  onlyOutlineAllFacades();

  if (isSegmentsCategory(categoryName)) {
    const measurements = yield select(measureModeDux.selectors.getMeasurements);
    const { items } = measurements[categoryName];
    const segmentsArray = flattenSegmentsItems(items);
    setDefaultVisibility(SEGMENTS_VISIBILITY_OPTS);
    addOutlineToSegments(segmentsArray);
  } else {
    setDefaultVisibility();
  }

  yield put(uiDux.actions.openExtendedSidebar());
  yield put(macheteDux.actions.panCamera(VIEW_OPTIONS.main.id));
}

export default function* sagas() {
  yield all([
    takeLatest(
      measureModeDux.actions.activateMeasureMode,
      activateMeasureModeSaga,
    ),
    takeLatest(
      measureModeDux.actions.setActiveCategoryName,
      setActiveCategoryNameSaga,
    ),
    takeLatest(appDux.actions.setSegmentsData, calculateMeasurementsSaga),
  ]);
}
